import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from '../lib/swal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DollarSign, ShoppingCart, Link as LinkIcon, LogOut, Home, Copy, CheckCircle, Lock, Building, CreditCard, Phone, Clock, User, AlertTriangle, ArrowRight, BarChart3, Wallet, Inbox, ChevronLeft, ChevronRight, ShieldCheck, Sparkles, ExternalLink } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VendedorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMounted = useRef(true);
  
  // Estados
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Estados para modales
  const [showDatosBancarios, setShowDatosBancarios] = useState(false);
  const [showEditarPerfil, setShowEditarPerfil] = useState(false);
  const [showCambiarPassword, setShowCambiarPassword] = useState(false);
  const [showSolicitarRetiro, setShowSolicitarRetiro] = useState(false);
  
  // Estados para formularios
  const [datosBancarios, setDatosBancarios] = useState({
    nombre_banco: '',
    tipo_cuenta: 'ahorro',
    numero_cuenta: ''
  });
  
  const [perfilData, setPerfilData] = useState({
    name: '',
    cedula: '',
    celular: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    actual: '',
    nueva: '',
    confirmar_password: ''
  });
  
  const [retiroData, setRetiroData] = useState({
    monto: ''
  });
  
  // Estados para movimientos
  const [movimientos, setMovimientos] = useState([]);
  const [filtroTipoMovimiento, setFiltroTipoMovimiento] = useState('todos');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalMovimientos, setTotalMovimientos] = useState(0);

  useEffect(() => {
    isMounted.current = true;
    const abortController = new AbortController();
    
    if (!user || user.role !== 'vendedor') {
      navigate('/login');
      return;
    }
    
    const loadPerfil = async () => {
      try {
        const response = await axios.get(`${API}/vendedor/perfil`, { 
          
          signal: abortController.signal
        });
        
        if (isMounted.current) {
          setPerfil(response.data);
          
          if (response.data.datos_bancarios_completos) {
            setDatosBancarios({
              nombre_banco: response.data.nombre_banco || '',
              tipo_cuenta: response.data.tipo_cuenta || 'ahorro',
              numero_cuenta: response.data.numero_cuenta || ''
            });
          }
          
          setPerfilData({
            name: response.data.name || '',
            cedula: response.data.cedula || '',
            celular: response.data.celular || ''
          });
          
          setLoading(false);
          // Load movements initially
          fetchMovimientos(1, abortController.signal);
        }
      } catch (error) {
        if (error.name !== 'CanceledError' && isMounted.current) {
          console.error('Error al cargar perfil:', error);
          toast.error('Error al cargar perfil');
          setLoading(false);
        }
      }
    };
    
    loadPerfil();
    
    return () => {
      isMounted.current = false;
      abortController.abort();
    };
  }, [user, navigate]);
  
  const fetchPerfil = async () => {
    if (!isMounted.current) return;
    try {
      const response = await axios.get(`${API}/vendedor/perfil`);
      if (isMounted.current) {
        setPerfil(response.data);
        if (response.data.datos_bancarios_completos) {
          setDatosBancarios({
            nombre_banco: response.data.nombre_banco || '',
            tipo_cuenta: response.data.tipo_cuenta || 'ahorro',
            numero_cuenta: response.data.numero_cuenta || ''
          });
        }
        setPerfilData({
          name: response.data.name || '',
          cedula: response.data.cedula || '',
          celular: response.data.celular || ''
        });
        fetchMovimientos(paginaActual);
      }
    } catch (error) {
      console.error('Error al cargar perfil:', error);
    }
  };
  
  const fetchMovimientos = async (page = 1, signal = null) => {
    if (!isMounted.current) return;
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      
      if (filtroTipoMovimiento !== 'todos') {
        params.append('tipo', filtroTipoMovimiento);
      }
      if (fechaDesde) {
        params.append('fecha_desde', new Date(fechaDesde).toISOString());
      }
      if (fechaHasta) {
        params.append('fecha_hasta', new Date(fechaHasta).toISOString());
      }
      
      const response = await axios.get(`${API}/vendedor/movimientos?${params}`, { 
        
        signal: signal
      });
      if (isMounted.current) {
        setMovimientos(response.data.movimientos);
        setTotalMovimientos(response.data.total);
        setTotalPaginas(response.data.total_pages);
        setPaginaActual(response.data.page);
      }
    } catch (error) {
      if (error.name !== 'CanceledError') {
        console.error('Error al cargar movimientos:', error);
        if (isMounted.current) {
          toast.error('Error al cargar movimientos');
        }
      }
    }
  };
  
  const handleBuscarMovimientos = () => {
    setPaginaActual(1);
    fetchMovimientos(1);
  };
  
  const handleCambiarPagina = (nuevaPagina) => {
    fetchMovimientos(nuevaPagina);
  };

  const handleLogout = async () => {
    if (isMounted.current) {
      setShowDatosBancarios(false);
      setShowEditarPerfil(false);
      setShowCambiarPassword(false);
      setShowSolicitarRetiro(false);
    }
    try {
      await logout();
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      if (isMounted.current) {
        navigate('/', { replace: true });
      }
    }
  };

  const copyLink = () => {
    if (!user || !user.id) return;
    const link = `${window.location.origin}/?vendedor=${user.id}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGuardarDatosBancarios = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/vendedor/datos-bancarios`, datosBancarios);
      toast.success('Datos bancarios actualizados');
      setShowDatosBancarios(false);
      fetchPerfil();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar datos bancarios');
    }
  };
  
  const handleEditarPerfil = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/vendedor/perfil`, perfilData);
      toast.success('Perfil actualizado correctamente');
      setShowEditarPerfil(false);
      fetchPerfil();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    }
  };

  const handleCambiarPassword = async (e) => {
    e.preventDefault();
    if (passwordData.nueva !== passwordData.confirmar_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    try {
      await axios.put(`${API}/vendedor/cambiar-password`, {
        password_actual: passwordData.actual,
        password_nueva: passwordData.nueva
      });
      toast.success('Contraseña actualizada correctamente');
      setShowCambiarPassword(false);
      setPasswordData({ actual: '', nueva: '', confirmar_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  const handleSolicitarRetiro = async (e) => {
    e.preventDefault();
    const monto = parseFloat(retiroData.monto);
    if (!monto || monto <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }
    if (monto > (perfil?.wallet_balance || 0)) {
      toast.error('Monto supera el saldo disponible');
      return;
    }
    try {
      await axios.post(`${API}/vendedor/solicitar-retiro`, { monto });
      toast.success('Solicitud de retiro enviada');
      setShowSolicitarRetiro(false);
      setRetiroData({ monto: '' });
      fetchPerfil();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al solicitar retiro');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white font-black uppercase tracking-[0.3em] text-[10px]">Cargando Dashboard...</p>
        </div>
      </div>
    );
  }

  const linkVendedor = user?.id ? `${window.location.origin}/?vendedor=${user.id}` : '';

  return (
    <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden pb-12">
      {/* 🔮 Ambient glow orbs */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-green-500/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-orange-600/5 blur-[100px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 relative z-10 pt-8">
        {/* Header */}
        <div className="relative mb-8 rounded-3xl bg-slate-900/50 backdrop-blur-xl border border-white/5 p-6 md:p-8 overflow-hidden group">
          <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
          
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-green-400" />
                </div>
                <h1 className="text-2xl md:text-3xl font-black uppercase tracking-tight italic">
                  Dashboard <span className="text-green-400">Vendedor</span>
                </h1>
              </div>
              <p className="text-slate-400 font-medium flex items-center gap-2">
                Bienvenido de nuevo, <span className="text-white font-bold">{perfil?.name || user?.name}</span>
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full md:w-auto">
              <ThemeToggle />
              <button 
                onClick={() => navigate('/')} 
                className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-[11px] font-black uppercase tracking-widest text-slate-300 hover:text-white hover:bg-white/10 transition-all"
              >
                <Home className="w-4 h-4" />
                Inicio
              </button>
              <button 
                onClick={handleLogout} 
                className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-red-500/20 bg-red-500/5 backdrop-blur-sm text-[11px] font-black uppercase tracking-widest text-red-400 hover:text-white hover:bg-red-500/20 transition-all"
              >
                <LogOut className="w-4 h-4" />
                Salir
              </button>
            </div>
          </div>
        </div>

        {/* Warning Alert */}
        {!perfil?.datos_bancarios_completos && (
          <div className="mb-8 relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000" />
            <div className="relative rounded-2xl bg-orange-500/10 border border-orange-500/30 p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
              </div>
              <div className="flex-1">
                <p className="text-orange-200 text-sm font-bold uppercase tracking-tight italic">Acción Requerida</p>
                <p className="text-orange-100/70 text-sm font-medium leading-tight">
                  Debes completar tus datos bancarios para poder solicitar retiros de comisiones.
                </p>
              </div>
              <button 
                onClick={() => setShowDatosBancarios(true)}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-400 text-black text-[10px] font-black uppercase tracking-widest rounded-lg transition-colors shrink-0 md:inline-block"
              >
                Completar ahora
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            { label: 'Mi Perfil', icon: User, color: 'indigo', onClick: () => setShowEditarPerfil(true) },
            { 
              label: perfil?.datos_bancarios_completos ? 'Datos Bancarios' : 'Completar Datos', 
              icon: Building, 
              color: 'blue', 
              onClick: () => setShowDatosBancarios(true) 
            },
            { label: 'Seguridad', icon: Lock, color: 'purple', onClick: () => setShowCambiarPassword(true) },
            { 
              label: 'Solicitar Retiro', 
              icon: Wallet, 
              color: 'green', 
              onClick: () => setShowSolicitarRetiro(true),
              disabled: !perfil?.datos_bancarios_completos || (perfil?.wallet_balance || 0) <= 0
            },
          ].map((btn) => (
            <button
              key={btn.label}
              onClick={btn.onClick}
              disabled={btn.disabled}
              className={cn(
                "group relative overflow-hidden flex items-center gap-3 p-5 rounded-2xl border transition-all duration-300 disabled:opacity-40 disabled:pointer-events-none",
                btn.color === 'indigo' && "bg-indigo-500/5 border-indigo-500/20 hover:border-indigo-500/40 hover:bg-indigo-500/10 text-indigo-400",
                btn.color === 'blue' && "bg-blue-500/5 border-blue-500/20 hover:border-blue-500/40 hover:bg-blue-500/10 text-blue-400",
                btn.color === 'purple' && "bg-purple-500/5 border-purple-500/20 hover:border-purple-500/40 hover:bg-purple-500/10 text-purple-400",
                btn.color === 'green' && "bg-green-500/5 border-green-500/20 hover:border-green-500/40 hover:bg-green-500/10 text-green-400",
              )}
            >
              <div className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-300",
                btn.color === 'indigo' && "bg-indigo-500/10 group-hover:bg-indigo-500/20",
                btn.color === 'blue' && "bg-blue-500/10 group-hover:bg-blue-500/20",
                btn.color === 'purple' && "bg-purple-500/10 group-hover:bg-purple-500/20",
                btn.color === 'green' && "bg-green-500/10 group-hover:bg-green-500/20",
              )}>
                <btn.icon className="w-5 h-5" />
              </div>
              <span className="text-xs md:text-sm font-black uppercase tracking-widest text-white">{btn.label}</span>
            </button>
          ))}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {/* Wallet Balance */}
          <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-gradient-to-br from-white/10 to-transparent">
            <div className="bg-slate-900/80 backdrop-blur-xl rounded-[calc(1.5rem-1px)] p-6 h-full relative overflow-hidden">
               <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-3xl pointer-events-none translate-x-1/4 -translate-y-1/4" />
               <div className="flex items-center gap-3 mb-6">
                 <div className="w-10 h-10 rounded-xl bg-green-500/15 border border-green-500/20 flex items-center justify-center">
                   <DollarSign className="w-5 h-5 text-green-400" />
                 </div>
                 <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Saldo Disponible</h3>
               </div>
               <div className="flex items-end justify-between gap-4">
                 <span className="text-4xl font-black text-white tracking-tighter">
                   <span className="text-green-500 mr-2">$</span>
                   {perfil?.wallet_balance?.toFixed(2) || '0.00'}
                 </span>
                 <Sparkles className="w-6 h-6 text-green-500/20 mb-2 group-hover:text-green-500/40 transition-colors" />
               </div>
            </div>
          </div>

          {/* Total Commissions */}
          <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-gradient-to-br from-white/10 to-transparent">
            <div className="bg-slate-900/80 backdrop-blur-xl rounded-[calc(1.5rem-1px)] p-6 h-full relative overflow-hidden">
               <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl pointer-events-none translate-x-1/4 -translate-y-1/4" />
               <div className="flex items-center gap-3 mb-6">
                 <div className="w-10 h-10 rounded-xl bg-orange-500/15 border border-orange-500/20 flex items-center justify-center">
                   <ShoppingCart className="w-5 h-5 text-orange-400" />
                 </div>
                 <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Total Comisiones</h3>
               </div>
               <div className="flex items-end justify-between gap-4">
                 <span className="text-4xl font-black text-white tracking-tighter">
                   <span className="text-orange-500 mr-2">$</span>
                   {perfil?.total_comisiones?.toFixed(2) || '0.00'}
                 </span>
                 <BarChart3 className="w-6 h-6 text-orange-500/20 mb-2 group-hover:text-orange-500/40 transition-colors" />
               </div>
            </div>
          </div>

          {/* Referral Link */}
          <div className="relative group p-[1px] rounded-3xl overflow-hidden bg-gradient-to-br from-white/10 to-transparent">
            <div className="bg-slate-900/80 backdrop-blur-xl rounded-[calc(1.5rem-1px)] p-6 h-full relative overflow-hidden flex flex-col justify-between">
               <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl pointer-events-none translate-x-1/4 -translate-y-1/4" />
               <div className="flex items-center gap-3 mb-4">
                 <div className="w-10 h-10 rounded-xl bg-blue-500/15 border border-blue-500/20 flex items-center justify-center">
                   <LinkIcon className="w-5 h-5 text-blue-400" />
                 </div>
                 <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Tu Link Exclusivo</h3>
               </div>
               
               <div className="relative">
                 <div className="p-3 bg-white/5 rounded-xl border border-white/5 text-[10px] text-slate-400 font-mono truncate mb-3 pr-10">
                   {linkVendedor}
                 </div>
                 <button 
                  onClick={copyLink}
                  className={cn(
                    "w-full h-11 rounded-2xl flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-300",
                    copied 
                      ? "bg-green-500 text-black" 
                      : "bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_20px_rgba(37,99,235,0.2)]"
                  )}
                 >
                   {copied ? (
                     <><CheckCircle className="w-4 h-4" /> Copiado!</>
                   ) : (
                     <><Copy className="w-4 h-4" /> Copiar Link</>
                   )}
                 </button>
               </div>
            </div>
          </div>
        </div>

        {/* Movement History Section */}
        <div className="relative rounded-3xl border border-white/5 bg-slate-900/50 backdrop-blur-xl overflow-hidden mb-12">
          <div className="p-6 md:p-8 border-b border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-slate-400" />
              </div>
              <h2 className="text-xl font-black uppercase tracking-tight italic">Historial de <span className="text-green-400">Movimientos</span></h2>
            </div>
            
            <Badge variant="outline" className="bg-white/5 border-white/10 text-slate-400 px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
              {totalMovimientos} Registros
            </Badge>
          </div>

          <div className="p-6 md:p-8">
            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="space-y-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Tipo de operación</Label>
                <Select value={filtroTipoMovimiento} onValueChange={setFiltroTipoMovimiento}>
                  <SelectTrigger className="h-11 bg-white/5 border-white/10 text-white rounded-xl focus:ring-green-500/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-white">
                    <SelectItem value="todos">Todos los tipos</SelectItem>
                    <SelectItem value="ingreso">Solo Ingresos</SelectItem>
                    <SelectItem value="egreso">Solo Egresos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Desde</Label>
                <Input 
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  className="h-11 bg-white/5 border-white/10 text-white rounded-xl focus:ring-green-500/20"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Hasta</Label>
                <Input 
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  className="h-11 bg-white/5 border-white/10 text-white rounded-xl focus:ring-green-500/20"
                />
              </div>
              
              <div className="flex items-end">
                <button 
                  onClick={handleBuscarMovimientos}
                  className="w-full h-11 bg-green-500 hover:bg-green-400 text-black text-[11px] font-black uppercase tracking-widest rounded-xl transition-all hover:scale-[1.02] shadow-[0_0_20px_rgba(34,197,94,0.2)]"
                >
                  Filtrar resultados
                </button>
              </div>
            </div>

            {/* Pagination Info */}
            {totalMovimientos > 0 && (
              <div className="flex items-center gap-2 mb-6 px-4">
                <div className="w-1 h-3 bg-green-500 rounded-full" />
                <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest">
                  Página {paginaActual} de {totalPaginas} · {movimientos.length} en vista
                </p>
              </div>
            )}

            {/* Movement list */}
            <div className={`space-y-4 ${movimientos.length > 5 ? 'max-h-[500px] overflow-y-auto pr-2 custom-scrollbar' : ''}`}>
              {movimientos.length === 0 ? (
                <div className="py-20 text-center flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-3xl bg-white/5 flex items-center justify-center opacity-40">
                    <Inbox className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-slate-500 text-sm font-medium italic">No se encontraron movimientos con los filtros aplicados</p>
                </div>
              ) : (
                movimientos.map((mov) => (
                  <div 
                    key={mov.id}
                    className={cn(
                      "group relative rounded-2xl border p-5 transition-all duration-300 overflow-hidden",
                      mov.tipo === 'ingreso' 
                        ? 'bg-green-500/5 border-green-500/10 hover:border-green-500/30' 
                        : 'bg-red-500/5 border-red-500/10 hover:border-red-500/30'
                    )}
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                      style={{ 
                        background: `radial-gradient(circle at 100% 0%, ${mov.tipo === 'ingreso' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)'}, transparent 70%)` 
                      }} />
                    
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
                      <div className="flex items-start gap-4 flex-1">
                        <div className={cn(
                          "w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border",
                          mov.tipo === 'ingreso' 
                            ? "bg-green-500/10 border-green-500/20 text-green-400" 
                            : "bg-red-500/10 border-red-500/20 text-red-400"
                        )}>
                          {mov.tipo === 'ingreso' ? <DollarSign className="w-6 h-6" /> : <CreditCard className="w-6 h-6" />}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                             <h4 className="text-sm font-black uppercase tracking-tight text-white truncate">
                               {mov.tipo === 'ingreso' ? '🤑 Comisión ganada' : '🏦 Pago de retiro'}
                             </h4>
                             <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 bg-white/5 px-2 py-0.5 rounded border border-white/5">
                               ID: {mov.id.slice(0, 8)}
                             </span>
                          </div>
                          
                          <p className="text-[11px] text-slate-400 font-medium mb-3 flex items-center gap-2">
                             <Clock className="w-3 h-3 text-slate-500" />
                             {new Date(mov.fecha).toLocaleString('es-EC', { 
                               dateStyle: 'medium', 
                               timeStyle: 'short' 
                             })}
                          </p>
                          
                          <div className="flex flex-wrap gap-2">
                            {mov.tipo === 'ingreso' ? (
                              <>
                                <div className="text-[10px] bg-green-500/10 text-green-400 font-bold px-3 py-1 rounded-lg border border-green-500/10">
                                  Ticket #{mov.numero_boleto}
                                </div>
                                <div className="text-[10px] bg-white/5 text-slate-300 font-bold px-3 py-1 rounded-lg border border-white/10">
                                  {mov.sorteo_titulo}
                                </div>
                                <div className="text-[10px] bg-white/5 text-slate-300 font-bold px-3 py-1 rounded-lg border border-white/10">
                                  Cliente: {mov.comprador_nombre}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-[10px] bg-red-500/10 text-red-400 font-bold px-3 py-1 rounded-lg border border-red-500/10">
                                  {mov.banco}
                                </div>
                                <div className="text-[10px] bg-white/5 text-slate-300 font-bold px-3 py-1 rounded-lg border border-white/10 uppercase italic tracking-widest">
                                  {mov.tipo_cuenta}
                                </div>
                                {mov.comprobante_url && (
                                  <a href={mov.comprobante_url} target="_blank" rel="noopener noreferrer" className="text-[10px] bg-blue-600 hover:bg-blue-500 text-white font-black px-3 py-1 rounded-lg transition-colors flex items-center gap-1">
                                    <ExternalLink className="w-3 h-3" /> Ver comprobante
                                  </a>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right w-full md:w-auto self-end md:self-center">
                        <span className={cn(
                          "text-3xl font-black tracking-tighter",
                          mov.tipo === 'ingreso' ? "text-green-500" : "text-red-500"
                        )}>
                          {mov.tipo === 'ingreso' ? '+' : '-'}${mov.monto.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Pagination Controls */}
            {totalPaginas > 1 && (
              <div className="flex justify-center items-center gap-4 mt-10">
                <button
                  onClick={() => handleCambiarPagina(paginaActual - 1)}
                  disabled={paginaActual === 1}
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center shadow-lg"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-1">
                  <span className="text-sm font-black text-white px-4 h-10 flex items-center bg-white/5 border border-white/10 rounded-xl tracking-widest">
                    {paginaActual} / {totalPaginas}
                  </span>
                </div>
                
                <button
                  onClick={() => handleCambiarPagina(paginaActual + 1)}
                  disabled={paginaActual === totalPaginas}
                  className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center shadow-lg"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── MODALS (Redesigned with Premium Dark Theme) ── */}
        
        {/* Modal Editar Perfil */}
        <Dialog open={showEditarPerfil} onOpenChange={setShowEditarPerfil}>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg rounded-3xl overflow-hidden p-0">
            <div className="px-8 pt-8 pb-6 border-b border-white/5 bg-slate-800/50">
               <div className="flex items-center gap-3 mb-2">
                 <div className="w-10 h-10 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                   <User className="w-5 h-5 text-indigo-400" />
                 </div>
                 <DialogHeader className="p-0 text-left">
                   <DialogTitle className="text-xl font-black uppercase tracking-tight italic">Información de <span className="text-indigo-400">Perfil</span></DialogTitle>
                 </DialogHeader>
               </div>
               <p className="text-slate-400 text-sm font-medium">Gestiona tus datos personales en la plataforma.</p>
            </div>
            
            <form onSubmit={handleEditarPerfil} className="p-8 space-y-5">
              <div className="space-y-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Nombre Completo</Label>
                <Input 
                  value={perfilData.name}
                  onChange={(e) => setPerfilData({...perfilData, name: e.target.value})}
                  required
                  placeholder="Ej: Juan Pérez"
                  className="h-12 bg-white/5 border-white/10 text-white rounded-xl focus:ring-indigo-500/20"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Cédula / ID</Label>
                  <Input 
                    value={perfilData.cedula}
                    onChange={(e) => setPerfilData({...perfilData, cedula: e.target.value})}
                    required
                    placeholder="Ej: 12345678"
                    className="h-12 bg-white/5 border-white/10 text-white rounded-xl focus:ring-indigo-500/20"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Celular</Label>
                  <Input 
                    value={perfilData.celular}
                    onChange={(e) => setPerfilData({...perfilData, celular: e.target.value})}
                    required
                    placeholder="0999999999"
                    className="h-12 bg-white/5 border-white/10 text-white rounded-xl focus:ring-indigo-500/20"
                  />
                </div>
              </div>
              
              <div className="bg-slate-950/50 p-4 rounded-2xl border border-white/5">
                <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1">Correo Electrónico Registrado</p>
                <p className="text-white text-sm font-bold flex items-center justify-between">
                  {perfil?.email} 
                  <ShieldCheck className="w-4 h-4 text-green-500 opacity-40 ml-2" />
                </p>
                <p className="text-[9px] text-slate-600 italic mt-2">Para cambiar el correo, contacta con soporte.</p>
              </div>
              
              <button 
                type="submit" 
                className="w-full h-14 mt-4 bg-indigo-600 hover:bg-indigo-500 text-white font-black uppercase tracking-widest rounded-2xl transition-all shadow-[0_0_20px_rgba(79,70,229,0.3)] hover:scale-[1.02] active:scale-[0.98]"
              >
                Guardar cambios en perfil
              </button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Datos Bancarios */}
        <Dialog open={showDatosBancarios} onOpenChange={setShowDatosBancarios}>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg rounded-3xl overflow-hidden p-0">
             <div className="px-8 pt-8 pb-6 border-b border-white/5 bg-slate-800/50">
               <div className="flex items-center gap-3 mb-2">
                 <div className="w-10 h-10 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                   <Building className="w-5 h-5 text-blue-400" />
                 </div>
                 <DialogHeader className="p-0 text-left">
                   <DialogTitle className="text-xl font-black uppercase tracking-tight italic">Cuentas para <span className="text-blue-400">Retiros</span></DialogTitle>
                 </DialogHeader>
               </div>
               <p className="text-slate-400 text-sm font-medium">Configura dónde quieres recibir tus comisiones.</p>
            </div>
            
            <form onSubmit={handleGuardarDatosBancarios} className="p-8 space-y-5">
              <div className="space-y-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Entidad Bancaria / Institución</Label>
                <Input 
                  value={datosBancarios.nombre_banco}
                  onChange={(e) => setDatosBancarios({...datosBancarios, nombre_banco: e.target.value})}
                  required
                  placeholder="Ej: Banco Pichincha, Guayaquil, etc."
                  className="h-12 bg-white/5 border-white/10 text-white rounded-xl focus:ring-blue-500/20"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Tipo de Cuenta</Label>
                <Select 
                  value={datosBancarios.tipo_cuenta}
                  onValueChange={(value) => setDatosBancarios({...datosBancarios, tipo_cuenta: value})}
                >
                  <SelectTrigger className="h-12 bg-white/5 border-white/10 text-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10 text-white">
                    <SelectItem value="ahorro">Cta. de Ahorros</SelectItem>
                    <SelectItem value="corriente">Cta. Corriente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Número de Cuenta</Label>
                <Input 
                  value={datosBancarios.numero_cuenta}
                  onChange={(e) => setDatosBancarios({...datosBancarios, numero_cuenta: e.target.value})}
                  required
                  placeholder="Ej: 21000XXXXX"
                  className="h-12 bg-white/5 border-white/10 text-white rounded-xl focus:ring-blue-500/20"
                />
              </div>
              
              <div className="rounded-2xl border border-blue-500/10 bg-blue-500/5 p-4 flex gap-3">
                <Sparkles className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                <p className="text-[11px] text-blue-200/70 font-medium leading-normal">
                  Esta información es almacenada de forma segura para gestionar tus solicitudes de retiro automáticamente.
                </p>
              </div>
              
              <button 
                type="submit" 
                className="w-full h-14 mt-4 bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-widest rounded-2xl transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:scale-[1.02] active:scale-[0.98]"
              >
                Actualizar datos bancarios
              </button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Cambiar Contraseña */}
        <Dialog open={showCambiarPassword} onOpenChange={setShowCambiarPassword}>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg rounded-3xl overflow-hidden p-0">
             <div className="px-8 pt-8 pb-6 border-b border-white/5 bg-slate-800/50">
               <div className="flex items-center gap-3 mb-2">
                 <div className="w-10 h-10 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                   <Lock className="w-5 h-5 text-purple-400" />
                 </div>
                 <DialogHeader className="p-0 text-left">
                   <DialogTitle className="text-xl font-black uppercase tracking-tight italic">Seguridad de <span className="text-purple-400">Cuenta</span></DialogTitle>
                 </DialogHeader>
               </div>
               <p className="text-slate-400 text-sm font-medium">Recomendamos cambiar tu contraseña periódicamente.</p>
            </div>
            
            <form onSubmit={handleCambiarPassword} className="p-8 space-y-5">
              <div className="space-y-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Contraseña Actual</Label>
                <Input 
                  type="password"
                  value={passwordData.actual}
                  onChange={(e) => setPasswordData({...passwordData, actual: e.target.value})}
                  required
                  placeholder="••••••••"
                  className="h-12 bg-white/5 border-white/10 text-white rounded-xl focus:ring-purple-500/20"
                />
              </div>
              
              <div className="h-px bg-white/5 my-2" />
              
              <div className="space-y-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Nueva Contraseña</Label>
                <Input 
                  type="password"
                  value={passwordData.nueva}
                  onChange={(e) => setPasswordData({...passwordData, nueva: e.target.value})}
                  required
                  minLength={6}
                  placeholder="Min. 6 caracteres"
                  className="h-12 bg-white/5 border-white/10 text-white rounded-xl focus:ring-purple-500/20"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Confirmar Nueva Contraseña</Label>
                <Input 
                  type="password"
                  value={passwordData.confirmar_password}
                  onChange={(e) => setPasswordData({...passwordData, confirmar_password: e.target.value})}
                  required
                  minLength={6}
                  placeholder="Repite la contraseña"
                  className="h-12 bg-white/5 border-white/10 text-white rounded-xl focus:ring-purple-500/20"
                />
              </div>
              
              <button 
                type="submit" 
                className="w-full h-14 mt-4 bg-purple-600 hover:bg-purple-500 text-white font-black uppercase tracking-widest rounded-2xl transition-all shadow-[0_0_20px_rgba(147,51,234,0.3)] hover:scale-[1.02] active:scale-[0.98]"
              >
                Actualizar clave de acceso
              </button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Solicitar Retiro */}
        <Dialog open={showSolicitarRetiro} onOpenChange={setShowSolicitarRetiro}>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg rounded-3xl overflow-hidden p-0">
             <div className="px-8 pt-8 pb-6 border-b border-white/5 bg-slate-800/50">
               <div className="flex items-center gap-3 mb-2">
                 <div className="w-10 h-10 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                   <Inbox className="w-5 h-5 text-green-400" />
                 </div>
                 <DialogHeader className="p-0 text-left">
                   <DialogTitle className="text-xl font-black uppercase tracking-tight italic">Nueva Solicitud de <span className="text-green-400">Retiro</span></DialogTitle>
                 </DialogHeader>
               </div>
               <p className="text-slate-400 text-sm font-medium">Extrae tus ganancias acumuladas ahora.</p>
            </div>
            
            <form onSubmit={handleSolicitarRetiro} className="p-8 space-y-6">
              <div className="space-y-1">
                 <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-2">Monto a retirar ($ USD)</Label>
                 <div className="relative">
                   <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                   <Input 
                    type="number"
                    step="0.01"
                    min="0"
                    max={perfil?.wallet_balance || 0}
                    value={retiroData.monto}
                    onChange={(e) => setRetiroData({...retiroData, monto: e.target.value})}
                    required
                    placeholder="0.00"
                    className="h-16 pl-12 bg-white/5 border-white/10 text-2xl font-black text-white rounded-2xl focus:ring-green-500/20"
                  />
                 </div>
              </div>
              
              <div className="rounded-2xl bg-slate-950 border border-white/5 p-4 divide-y divide-white/5">
                <div className="flex items-center justify-between pb-3">
                  <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">Saldo total</span>
                  <span className="text-sm font-black text-white">${perfil?.wallet_balance?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex items-center justify-between pt-3">
                  <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">Saldo restante</span>
                  <span className="text-sm font-black text-slate-400 italic">
                    ${Math.max(0, (perfil?.wallet_balance || 0) - (parseFloat(retiroData.monto) || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
              
              <button 
                type="submit" 
                className="w-full h-14 bg-green-500 hover:bg-green-400 text-black font-black uppercase tracking-widest rounded-2xl transition-all shadow-[0_0_30px_rgba(34,197,94,0.3)] hover:scale-[1.02] active:scale-[0.98]"
              >
                Confirmar y enviar solicitud
              </button>
              
              <p className="text-center text-[9px] text-slate-500 font-black uppercase tracking-widest">
                El pago se procesará en las próximas 24-48 horas hábiles.
              </p>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default VendedorDashboard;
